import Link from 'next/link';
import { useRouter } from 'next/router';
import { Pin, Plus, Search } from 'lucide-react';
import { IconButton } from '../../ui';
import { ConversationsState } from '../../../../lib/conversations';
import { useBasePath } from '../../../../lib/use-base-path';
import { useTranslation } from '../../../../lib/i18n';
import { markNavFromDrawer } from '../../../../lib/drawer-nav';
import { useLeaveChatOnNavigate } from '../../../../lib/use-leave-chat';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useConversationActions, CONVERSATIONS_PAGE, type ConversationActionsFlow } from '../../../../lib/use-conversation-actions';
import { ConversationRowMenu } from '../../conversations/ConversationRowMenu';
import { DeleteConversationConfirm } from '../../conversations/DeleteConversationConfirm';
import { RenameConversationDialog } from '../../conversations/RenameConversationDialog';
import { useRecentChats, type RecentChatModel } from '../hooks/use-recent-chats';
import { useSidebarNav } from '../hooks/use-sidebar-nav';
import { RECENT_CHATS_DEFAULT_OPEN } from '../shared';
import { RailTooltip } from './RailTooltip';
import styles from './RailChats.module.css';

// The rail's first row (Figma `Sidebar-Menu` 22535:282599): "Chats", which opens the Chats screen, with
// Search and New Chat beside it. Collapsed it is the Search and New Chat glyphs.
export function RailChatsHeader({ collapsed = false, onOpenSearch }: { collapsed?: boolean; onOpenSearch?: () => void }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const router = useRouter();
  const leaveChat = useLeaveChatOnNavigate();
  const { logPageClickBi } = useGenericBi();
  const { newChat } = useSidebarNav();
  const searchLabel = t('nav.search');
  const openSearch = () => {
    logPageClickBi({ page_name: 'menu', button_name: 'open_search', origin: 'menu' });
    onOpenSearch?.();
  };

  // Open, the buttons draw no hover of their own (the row's fill is the hover) and name themselves with
  // the rail's tooltip instead (design QA, Monday 13440219762).
  const quiet = collapsed ? undefined : styles.quiet;
  const search = onOpenSearch && (
    <IconButton size={collapsed ? 'medium' : 'tiny'} aria-label={searchLabel} onClick={openSearch} className={quiet}>
      <Search size={16} aria-hidden />
    </IconButton>
  );
  const add = (
    <IconButton size={collapsed ? 'medium' : 'tiny'} aria-label={newChat.label} disabled={newChat.disabled} onClick={newChat.onClick} className={quiet}>
      <Plus size={16} aria-hidden />
    </IconButton>
  );

  if (collapsed) {
    return (
      <div className={styles.collapsed}>
        {search && <RailTooltip label={searchLabel}>{search}</RailTooltip>}
        <RailTooltip label={newChat.label}>{add}</RailTooltip>
      </div>
    );
  }
  return (
    <div className={styles.header}>
      <Link
        href="/chats"
        as={`${basePath}/chats`}
        aria-current={router.pathname === '/chats' ? 'page' : undefined}
        className={styles.heading}
        onClick={() => {
          logPageClickBi({ page_name: 'menu', button_name: 'chats', origin: 'menu' });
          markNavFromDrawer();
          leaveChat();
        }}
      >
        {t('chats.title')}
      </Link>
      <span className={styles.actions}>
        {search && <RailTooltip label={searchLabel} placement="below">{search}</RailTooltip>}
        <RailTooltip label={newChat.label} placement="below">{add}</RailTooltip>
      </span>
    </div>
  );
}

// The pinned chats (Figma `Sidebar-Menu` 22570:313913): a pin and the title per row, and the row's ⋮ menu
// on hover. Only pins: the Chats screen above IS the history, so a recency list here would be a worse copy
// of it. Collapsed it is one pin glyph that opens the rail, shown only when something is pinned.
//
// The delete and rename dialogs are mounted ONCE here rather than per row — two mounted dialogs sharing one
// flow would both open on the same request and fight over the overlay.
export function RailPinnedChats({
  state,
  selectedId,
  collapsed = false,
  onExpand,
}: {
  state: ConversationsState;
  selectedId: string | null;
  collapsed?: boolean;
  onExpand?: () => void;
}) {
  const flow = useConversationActions(CONVERSATIONS_PAGE);
  const { chats, label } = useRecentChats({ state, selectedId, defaultOpen: RECENT_CHATS_DEFAULT_OPEN, section: 'pinned' });

  if (collapsed) {
    if (chats.length === 0 || !onExpand) return null;
    return (
      <RailTooltip label={label}>
        <IconButton aria-label={label} onClick={onExpand} className={styles.pin}>
          <Pin size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
        </IconButton>
      </RailTooltip>
    );
  }
  if (chats.length === 0) return null;
  return (
    <section className={styles.rows} aria-label={label}>
      {chats.map((chat) => (
        <PinnedChatRow key={chat.conv.id} chat={chat} flow={flow} />
      ))}
      <DeleteConversationConfirm flow={flow} />
      <RenameConversationDialog flow={flow} />
    </section>
  );
}

// The pin is the row's marker, not a control: Unpin lives in the row's menu with Rename and Delete, so a
// press beside the title can never unpin by accident.
function PinnedChatRow({ chat, flow }: { chat: RecentChatModel; flow: ConversationActionsFlow }) {
  const { t } = useTranslation();
  return (
    <div className={styles.row} data-current={chat.isSelected ? 'true' : undefined}>
      <span aria-hidden className={styles.pin}>
        <Pin size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </span>
      <Link href={chat.route} as={chat.path} shallow onClick={chat.onClick} aria-current={chat.isSelected ? 'page' : undefined} className={styles.link}>
        <span className={styles.title}>{chat.title}</span>
        {chat.channelBadge && <span className={styles.channel}>{chat.channelBadge}</span>}
      </Link>
      <span className={styles.menu}>
        <RailTooltip label={t('chat.moreActions')} placement="below">
          <ConversationRowMenu conv={chat.conv} title={chat.title} flow={flow} triggerSize="tiny" triggerClassName={styles.quiet} />
        </RailTooltip>
      </span>
    </div>
  );
}
